import { logger } from '@/lib/logger';

/**
 * Optimize SVG content for better display
 * Particularly useful for CAD file conversions where strokes may be too thick
 */
export function optimizeSvgStrokes(svgText: string): string {
  let optimized = svgText;
  
  logger.debug('Starting SVG stroke optimization...');
  
  // Count strokes before optimization
  const strokeMatches = svgText.match(/stroke-width[=:]["'\s]*([0-9.]+)/gi) || [];
  logger.debug(`Found ${strokeMatches.length} stroke-width definitions`);
  
  // Replace thick strokes with thinner ones in stroke-width attributes
  optimized = optimized.replace(/stroke-width\s*=\s*["']?([0-9.]+)["']?/gi, (match, widthStr) => {
    const width = parseFloat(widthStr);
    // Ultra-aggressive reduction for CAD files
    let newWidth: number;
    if (width > 10) {
      newWidth = 0.1; // Very thick strokes
    } else if (width > 5) {
      newWidth = 0.08; // Thick strokes
    } else if (width > 1) {
      newWidth = 0.06; // Medium strokes
    } else if (width >= 0.5) {
      newWidth = 0.05; // "Thin" strokes that still appear thick
    } else if (width >= 0.2) {
      newWidth = 0.03; // Already thin strokes
    } else {
      newWidth = width * 0.5; // Very thin strokes, reduce by half
    }
    // Removed per-stroke logging to reduce console spam with large CAD files
    return `stroke-width="${newWidth}"`;
  });
  
  // Also handle stroke-width in style attributes
  optimized = optimized.replace(/stroke-width\s*:\s*([0-9.]+)([^;]*)/gi, (match, widthStr, unit) => {
    const width = parseFloat(widthStr);
    let newWidth: number;
    if (width > 10) {
      newWidth = 0.1;
    } else if (width > 5) {
      newWidth = 0.08;
    } else if (width > 1) {
      newWidth = 0.06;
    } else if (width >= 0.5) {
      newWidth = 0.05;
    } else if (width >= 0.2) {
      newWidth = 0.03;
    } else {
      newWidth = width * 0.5;
    }
    return `stroke-width: ${newWidth}${unit}`;
  });
  
  // Handle inline styles with stroke-width
  optimized = optimized.replace(/style\s*=\s*["']([^"']*)/gi, (match, styleContent) => {
    const updatedStyle = styleContent.replace(/stroke-width\s*:\s*([0-9.]+)([^;]*)/gi, (strokeMatch: string, widthStr: string, unit: string) => {
      const width = parseFloat(widthStr);
      let newWidth: number;
      if (width > 10) {
        newWidth = 0.1;
      } else if (width > 5) {
        newWidth = 0.08;
      } else if (width > 1) {
        newWidth = 0.06;
      } else if (width >= 0.5) {
        newWidth = 0.05;
      } else if (width >= 0.2) {
        newWidth = 0.03;
      } else {
        newWidth = width * 0.5;
      }
      return `stroke-width: ${newWidth}${unit}`;
    });
    return `style="${updatedStyle}`;
  });
  
  // Count strokes after optimization
  const newStrokeMatches = optimized.match(/stroke-width[=:]["'\s]*([0-9.]+)/gi) || [];
  logger.debug(`After optimization: ${newStrokeMatches.length} stroke-width definitions`);
  
  // Add a default stroke-width to the root SVG element if not present
  // This ensures all strokes without explicit width are thin
  if (optimized.includes('<svg') && !optimized.match(/<svg[^>]*stroke-width/i)) {
    optimized = optimized.replace(/<svg([^>]*)>/i, '<svg$1 stroke-width="0.05">');
    logger.debug('Added default stroke-width="0.05" to SVG root');
  }
  
  // Also check for any stroke attributes without stroke-width and add a default
  // This handles cases where stroke is defined but stroke-width is not
  optimized = optimized.replace(/stroke\s*=\s*["']([^"']+)["'](?![^>]*stroke-width)/gi, (match) => {
    return match + ' stroke-width="0.05"';
  });
  
  return optimized;
}